import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@space/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Avatar storage key (`avatars/{userId}/{hash}.webp`).
   *
   * It lives here and isn't read from the session on purpose: the session is served by the API
   * and stays cached, so a freshly uploaded photo wouldn't be reflected until the
   * next refresh. Reading it from the local row — same as language, font,
   * and theme — the photo appears instantly and survives offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * User's leaderboard consent (privacy opt-in).
 */
export function getUserLeaderboardOptInQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      leaderboard_opt_in: user.leaderboard_opt_in,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * ALWAYS filters by `userId`, same as the rest of this module's `user` reads.
 * It used to decide the filter in a branch and the guest was left with a bare
 * `SELECT … FROM user LIMIT 1`: without `WHERE`, SQLite returns the FIRST
 * row it finds convenient, which doesn't have to be the guest's. That isn't a
 * correction failure but a data leak between accounts: during the
 * guest→real upgrade the local database pulls down a `user` row for each co-member of the
 * organization, so the guest ended up rendering ANOTHER person's theme.
 * Without `WHERE` there's no possible empty result: someone's row always comes out.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
